import ApplicationComponent from "legacy/ApplicationComponent"
import FormGroup from "legacy/FormGroup"
import Button from "legacy/LegacyButton"
import { inchesToFeet, isEmpty, isPresent } from "legacy/helpers.es6"
import MarinaMapView from "legacy/marina-map/MarinaMapView"
import { validatorFactory } from "legacy/validatorFactory"
import PropTypes from "prop-types"
import React from "react"

const partition = (array, predicate) => {
  const pass = []
  const fail = []
  array.forEach((item) => {
    if (predicate(item)) {
      pass.push(item)
    } else {
      fail.push(item)
    }
  })
  return [pass, fail]
}

/**
 * MarinaMapAnnotationController creates space geometry data with the SpaceGeometry form and also receives space geometry data from the MarinaMapsController and disseminates that data to MarinaMapView to render.
 * @param {object} map - Map that is currently being annotated.
 * @param {array} savedSpaceGeometries - an array of the pre-existing space geometries associated with the current map.
 * @param {array} spaces - spaces for current marina ordered by dockwalk view.
 * @param {string} mapImage - The url for the map image.
 * @param {string} settingsEndPoint - Endpoint to return to the marina map settings page.
 * @param {string} geometryEndpoint - Endpoint to direct calls for creating, updating, and destroying space geometries.
 * @param {string} boatImage - url for boat image for space rectangle.
 * @param {string} linearDockImage - url for dock image used for linear space rectangle.
 */

class MarinaMapAnnotationController extends ApplicationComponent {
  constructor(props) {
    super(props)
    this.state = {
      formData: this.defaultSpaceGeometryData(),
      publishingMap: false,
      spacesWithGeometries: this.mergeSpaceData(
        this.props.spaces,
        this.props.savedSpaceGeometries
      ),
      bulkAction: null,
      performingBulkAction: false,
      bulkAddSpaces: [],
      bulkEditingSpaces: [],
    }
    this.destroySpaceGeometry = this.destroySpaceGeometry.bind(this)
    this.saveSpace = this.saveSpace.bind(this)
    this.flipBoatOrientation = this.flipBoatOrientation.bind(this)
    this.editingNewSpace = this.editingNewSpace.bind(this)
    this.editingExistingSpace = this.editingExistingSpace.bind(this)
    this.cancelEdit = this.cancelEdit.bind(this)
    this.mergeSpaceData = this.mergeSpaceData.bind(this)
    this.publishMap = this.publishMap.bind(this)
    this.setFormState = this.setFormState.bind(this)
    this.validator = validatorFactory({ autoForceUpdate: this })
    this.handleKeyPress = this.handleKeyPress.bind(this)
    this.handleBulkAction = this.handleBulkAction.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleBulkSave = this.handleBulkSave.bind(this)
    this.handleBulkDestroy = this.handleBulkDestroy.bind(this)
    this.cancelBulkAction = this.cancelBulkAction.bind(this)
    this.handleSelectAllSpaces = this.handleSelectAllSpaces.bind(this)
    this.bulkAdding = this.bulkAdding.bind(this)
    this.spaces = this.spaces.bind(this)
    this.calculateBoundingRectData = this.calculateBoundingRectData.bind(this)
    this.toggleBulkAddSpace = this.toggleBulkAddSpace.bind(this)
    this.calculateBulkSeparation = this.calculateBulkSeparation.bind(this)
    this.calculateBulkOrientation = this.calculateBulkOrientation.bind(this)
    this.isDiagonal = this.isDiagonal.bind(this)
    this.isHorizontalOrVertical = this.isHorizontalOrVertical.bind(this)
    this.renderSidebarContent = this.renderSidebarContent.bind(this)
    this.renderSidebar = this.renderSidebar.bind(this)
    this.renderHeader = this.renderHeader.bind(this)
    this.renderUnannotatedSpaces = this.renderUnannotatedSpaces.bind(this)
    this.renderAnnotatedSpaces = this.renderAnnotatedSpaces.bind(this)
    this.renderSpaces = this.renderSpaces.bind(this)
    this.renderGeometryForm = this.renderGeometryForm.bind(this)
    this.renderSpaceGroupData = this.renderSpaceGroupData.bind(this)
    this.renderBulkActions = this.renderBulkActions.bind(this)
    this.addWidthOrHeight = this.addWidthOrHeight.bind(this)
  }

  componentDidMount() {
    const navbar = document.querySelector(".navbar")
    navbar.style.display = "none"
    document.addEventListener("keydown", this.handleKeyPress)
  }

  componentWillUnMount() {
    document.removeEventListener("keydown", this.handleKeyPress)
  }

  defaultSpaceGeometryData() {
    if (!isEmpty(this.props.savedSpaceGeometries)) {
      const savedGeometries = this.props.savedSpaceGeometries
      const lastGeometryData = savedGeometries[savedGeometries.length - 1].data
      return {
        x: parseInt(lastGeometryData.x) + 20,
        y: parseInt(lastGeometryData.y) + 20,
        height: lastGeometryData.height,
        width: lastGeometryData.width,
        rotation: lastGeometryData.rotation,
      }
    } else {
      return {
        x: 1400,
        y: 900,
        height: 100,
        width: 200,
        rotation: 0,
      }
    }
  }

  addWidthOrHeight(rotation, width, height, separation, index) {
    if (this.isHorizontalOrVertical(parseInt(rotation))) {
      return (parseInt(separation) + parseInt(height)) * index
    } else if (this.isDiagonal(parseInt(rotation))) {
      return (parseInt(separation) + parseInt(width)) * index
    }
  }

  setFormState(x, y) {
    if (this.state.performingBulkAction) {
      const spaceGeometries = this.state.spacesWithGeometries.filter(
        (space) => space.bulkSpace
      )
      const boundingRectData = this.calculateBoundingRectData(spaceGeometries)
      const newSpaceGeometries = spaceGeometries.map((space, index) => {
        const { data } = space
        const offsetX = data.x - boundingRectData.x
        const offsetY = data.y - boundingRectData.y

        space.data = {
          ...data,
          x: parseInt(x) + offsetX,
          y: parseInt(y) + offsetY,
        }
        return space
      })
      this.setState({
        spacesWithGeometries: [
          ...this.state.spacesWithGeometries.filter(
            (space) => !space.bulkSpace
          ),
          ...newSpaceGeometries,
        ],
        boundingRectData: this.calculateBoundingRectData(newSpaceGeometries),
      })
    }
    this.setState({ formData: { ...this.state.formData, x, y } })
  }

  handleKeyPress(event) {
    if (this.state.editing && !this.state.savingSpace) {
      if (event.key === "Enter") {
        if (this.state.performingBulkAction) {
          this.handleBulkSave()
        } else {
          this.saveSpace()
        }
      } else if (event.key === "Escape") {
        if (this.state.performingBulkAction) {
          this.cancelBulkAction()
        } else {
          this.cancelEdit()
        }
      }
    }
  }

  /** takes in spaces and existing space geometries and returns an array that contains objects that have space geometry data and space name. */
  mergeSpaceData(spaces, spaceGeometries) {
    const spaceIds = spaces.map((space) => space.id)
    const spaceGeometrySpaceIds = spaceGeometries.map(
      (geometry) => geometry.space_id
    )
    const spacesWithSavedGeometries = spaceGeometrySpaceIds.map((id) => {
      let newSpaceGeo
      if (spaceIds.includes(id)) {
        const space = spaces.find((s) => s.id === id)
        const geometry = spaceGeometries.find((g) => g.space_id === id)
        newSpaceGeo = {
          ...geometry,
          name: space.name,
          spaceGroup: space.spaceGroup,
        }
      }
      return newSpaceGeo
    })
    return spacesWithSavedGeometries
  }

  /** takes a space param and removes it from the existing spacesWithGeometries array while editing, and sets the current spaceGeometry form data to the data associated with that space.
   * @param {object} space - an object that represents a space with space geometries already saved to it */
  editingExistingSpace(space) {
    let spacesWithGeometries = this.state.spacesWithGeometries
    spacesWithGeometries = spacesWithGeometries.filter(
      (item) => item.space_id !== space.space_id
    )
    if (this.state.editing && this.state.savedSpace) {
      spacesWithGeometries = [...spacesWithGeometries, this.state.spaceEditing]
    }
    this.setState({
      editing: true,
      savedSpace: true,
      spaceEditing: space,
      spacesWithGeometries,
      formData: space.data,
    })
  }

  /**
   * takes a space param and creates a new spaceEditing object that has space and space geometry data. Also denotes in state that it is a new space that has not previously been saved.
   * @param {object} space - an object that comes directly from spaces ordered by dockwalk and does not have any space geometry data.
   */
  editingNewSpace(space) {
    this.setState({
      editing: true,
      savedSpace: false,
      spaceEditing: {
        ...space,
        space_id: space.id,
        newSpace: true,
      },
    })
  }

  /**
   * takes a space param and removes it from the spacesWithGeometries array in state and also destroys the space geometry record.
   * @param {object} spaceGeometry - an object that has space and space geometry data
   */
  destroySpaceGeometry(spaceGeometry) {
    this.setState({
      destroyingSpaceGeometry: true,
    })
    const params = {
      id: spaceGeometry.id,
      space_id: spaceGeometry.space_id,
      marina_map_id: this.props.map.id,
    }
    const spacesWithGeometries = this.state.spacesWithGeometries.filter(
      (item) => item.id !== spaceGeometry.id
    )
    // Note: jQuery ($) is loaded globally
    $.delete(
      `${this.props.geometryEndpoint}/${spaceGeometry.id}`,
      params,
      () => {
        this.setState({
          spacesWithGeometries,
          destroyingSpaceGeometry: false,
          savedSpace: false,
          editing: false,
        })
      }
    )
  }

  /**
   * automatically flips the annotating space image 180 degrees. adds 180 degrees when below 180 and subtracts when equal to or above 180.
   */
  flipBoatOrientation() {
    if (isPresent(this.state.formData.rotation)) {
      let endRotation = 0
      const currentRotation = parseInt(this.state.formData.rotation)
      if (currentRotation >= 180) {
        endRotation = currentRotation - 180
      } else {
        endRotation = currentRotation + 180
      }
      this.setState({
        formData: {
          ...this.state.formData,
          rotation: endRotation,
        },
      })
      if (this.state.performingBulkAction) {
        const spaceGeometries = this.state.spacesWithGeometries.filter(
          (space) => space.bulkSpace
        )
        const newSpaceGeometries = spaceGeometries.map((space, index) => {
          return {
            ...space,
            data: {
              ...space.data,
              rotation: endRotation,
            },
          }
        })
        this.setState({
          spacesWithGeometries: [
            ...this.state.spacesWithGeometries.filter(
              (space) => !space.bulkSpace
            ),
            ...newSpaceGeometries,
          ],
          boundingRectData: this.calculateBoundingRectData(newSpaceGeometries),
        })
      }
    }
  }

  /**
   * cancels the editing flow and puts the space that was being edited back in the spacesWithGeometries array in state, if it was a previously saved space.
   */
  cancelEdit() {
    if (this.state.spaceEditing.data && !this.state.spaceEditing.newSpace) {
      this.setState({
        editing: false,
        spacesWithGeometries: [
          ...this.state.spacesWithGeometries,
          this.state.spaceEditing,
        ],
      })
    } else {
      this.setState({
        editing: false,
      })
    }
  }

  /**
   * creates new space geometry associated with the current map or updates the space that is currently being edited.
   */
  saveSpace() {
    this.validator.showMessages()
    if (this.validator.allValid()) {
      const params = {
        space_id: this.state.spaceEditing.space_id,
        marina_map_id: this.props.map.id,
        data: this.state.formData,
      }
      this.setState({
        savingSpace: true,
      })
      if (this.state.spaceEditing.newSpace) {
        // Note: jQuery ($) is loaded globally
        $.post(this.props.geometryEndpoint, params, (data) => {
          this.setState({
            formData: {
              ...this.state.formData,
              x: parseInt(this.state.formData.x) + 20,
              y: parseInt(this.state.formData.y) + 20,
            },
            savingSpace: false,
            editing: false,
            spacesWithGeometries: [
              ...this.state.spacesWithGeometries,
              {
                ...data.spaceGeometry,
                ...this.state.spaceEditing,
                id: data.spaceGeometry.id,
                space_id: this.state.spaceEditing.id,
                newSpace: false,
              },
            ],
          })
        }).fail(() => {
          alert("The space geometries were unable to be saved.")
          this.setState({
            savingSpace: false,
          })
        })
      } else {
        // Note: jQuery ($) is loaded globally
        $.patch(
          `${this.props.geometryEndpoint}/${this.state.spaceEditing.id}`,
          { ...params, id: this.state.spaceEditing.id },
          () => {
            this.setState({
              formData: {
                ...this.state.formData,
                x: parseInt(this.state.formData.x) + 20,
                y: parseInt(this.state.formData.y) + 20,
              },
              savingSpace: false,
              savedSpace: false,
              editing: false,
              spacesWithGeometries: [
                ...this.state.spacesWithGeometries,
                { ...this.state.spaceEditing, data: this.state.formData },
              ],
            })
          }
        ).fail(() => {
          alert("The space geometries were unable to be saved.")
          this.setState({
            savingSpace: false,
          })
        })
      }
    }
  }

  /**
   * cancels the bulk action flow and resets the state to the previous state before the bulk action was initiated.
   */
  cancelBulkAction() {
    const withoutBulkSpaces = this.state.spacesWithGeometries.filter(
      (space) => !space.bulkSpace
    )

    this.setState({
      performingBulkAction: false,
      editing: false,
      bulkAction: null,
      spacesWithGeometries: this.bulkAdding()
        ? withoutBulkSpaces
        : [...withoutBulkSpaces, ...this.state.bulkEditingSpaces],
      bulkAddSpaces: [],
    })
  }

  /**
   * handles creating multiple space geometries at once. It takes the space geometries that are marked as bulkSpace and sends them to the server to be saved.
   */
  handleBulkSave() {
    this.validator.showMessages()
    if (this.validator.allValid()) {
      const spaceGeometries = this.state.spacesWithGeometries.filter(
        (space) => space.bulkSpace
      )
      const params = {
        space_geometries: spaceGeometries,
        marina_map_id: this.props.map.id,
      }
      this.setState({
        savingSpace: true,
      })
      if (this.bulkAdding()) {
        // Note: jQuery ($) is loaded globally
        $.post(this.props.bulkCreateGeometryEndpoint, params, (data) => {
          this.setState({
            spacesWithGeometries: this.state.spacesWithGeometries.map(
              (space) => {
                const spaceGeometry = data.spaceGeometries.find(
                  (g) => g.space_id === space.space_id
                )
                if (space.bulkSpace) {
                  return {
                    ...space,
                    bulkSpace: false,
                    id: spaceGeometry.id,
                  }
                } else {
                  return space
                }
              }
            ),
            performingBulkAction: false,
            editing: false,
            bulkAddSpaces: [],
            savingSpace: false,
          })
        }).fail(() => {
          alert("The space geometries were unable to be saved.")
          this.setState({
            savingSpace: false,
          })
        })
      } else {
        // Note: jQuery ($) is loaded globally
        $.patch(this.props.bulkUpdateGeometryEndpoint, params, (data) => {
          const spacesWithGeometries = this.state.spacesWithGeometries.map(
            (space) => {
              if (space.bulkSpace) {
                return {
                  ...space,
                  bulkSpace: false,
                }
              } else {
                return space
              }
            }
          )
          this.setState({
            spacesWithGeometries,
            performingBulkAction: false,
            editing: false,
            bulkAddSpaces: [],
            savingSpace: false,
            bulkEditingSpaces: [],
          })
        }).fail(() => {
          alert("The space geometries were unable to be saved.")
          this.setState({
            savingSpace: false,
          })
        })
      }
    }
  }

  handleBulkDestroy() {
    const spaceGeometries = this.state.spacesWithGeometries.filter(
      (space) => space.bulkSpace
    )
    const params = {
      space_geometries: spaceGeometries,
      marina_map_id: this.props.map.id,
    }
    this.setState({
      destroyingSpaceGeometry: true,
    })
    // Note: jQuery ($) is loaded globally
    $.delete(this.props.bulkDestroyGeometryEndpoint, params, () => {
      this.setState({
        spacesWithGeometries: this.state.spacesWithGeometries.filter(
          (space) => !space.bulkSpace
        ),
        destroyingSpaceGeometry: false,
        performingBulkAction: false,
        editing: false,
        bulkAddSpaces: [],
        bulkEditingSpaces: [],
      })
    }).fail(() => {
      alert("The space geometries were unable to be destroyed.")
      this.setState({
        destroyingSpaceGeometry: false,
      })
    })
  }

  publishMap() {
    this.setState({ publishingMap: true })
    // Note: jQuery ($) is loaded globally
    $.patch(this.props.publishMapEndpoint, {}, (data) => {
      window.location.href = data.redirect_to
    }).fail((data) => {
      this.setState({ publishingMap: false })
      alert(data.responseJSON.errors)
    })
  }

  toggleBulkAddSpace(spaceId) {
    if (this.state.bulkAddSpaces.includes(spaceId)) {
      this.setState({
        bulkAddSpaces: this.state.bulkAddSpaces.filter((id) => id !== spaceId),
      })
      if (!this.bulkAdding()) {
        this.setState({
          bulkEditingSpaces: this.state.bulkEditingSpaces.filter(
            (space) => space.space_id !== spaceId
          ),
        })
      }
    } else {
      this.setState({ bulkAddSpaces: [...this.state.bulkAddSpaces, spaceId] })
      if (!this.bulkAdding()) {
        this.setState({
          bulkEditingSpaces: [
            ...this.state.bulkEditingSpaces,
            this.state.spacesWithGeometries.find((g) => g.space_id === spaceId),
          ],
        })
      }
    }
  }

  /**
   *
   * @param {array} spaceGeometries
   * @returns boundingRectData
   * takes in space geometries and calculates the smallest rectangle (bounding box) that can contain all the given geometries.
   */
  calculateBoundingRectData(spaceGeometries) {
    const corners = spaceGeometries.flatMap((geometry) => {
      const { x, y, width, height, rotation } = geometry.data
      const radians = (rotation * Math.PI) / 180
      const cos = Math.cos(radians)
      const sin = Math.sin(radians)
      const halfWidth = parseInt(width) / 2
      const halfHeight = parseInt(height) / 2
      const cx = parseInt(x) + halfWidth
      const cy = parseInt(y) + halfHeight

      return [
        {
          x: cx + halfWidth * cos - halfHeight * sin,
          y: cy + halfWidth * sin + halfHeight * cos,
        },
        {
          x: cx - halfWidth * cos - halfHeight * sin,
          y: cy - halfWidth * sin + halfHeight * cos,
        },
        {
          x: cx + halfWidth * cos + halfHeight * sin,
          y: cy + halfWidth * sin - halfHeight * cos,
        },
        {
          x: cx - halfWidth * cos + halfHeight * sin,
          y: cy - halfWidth * sin - halfHeight * cos,
        },
      ]
    })

    const minX = Math.min(...corners.map((corner) => corner.x))
    const minY = Math.min(...corners.map((corner) => corner.y))
    const maxX = Math.max(...corners.map((corner) => corner.x))
    const maxY = Math.max(...corners.map((corner) => corner.y))

    const boundingRectData = {
      x: parseInt(minX),
      y: parseInt(minY),
      width: parseInt(maxX - minX),
      height: parseInt(maxY - minY),
      rotation: 0,
    }

    return boundingRectData
  }

  bulkAdding() {
    return this.state.bulkAction === "add"
  }

  /**
   * creates space geometries for the spaces that are marked as bulkSpace and adds them to the spacesWithGeometries array in state.
   * this action occurs when the user clicks the "Next" button in the bulk action flow.
   */
  handleBulkAction() {
    const spaceIds = this.state.bulkAddSpaces
    const separation = 10
    let spacesWithGeometries = this.state.spacesWithGeometries
    const spaceGeometries = spaceIds.map((id, index) => {
      let space = {}

      if (this.bulkAdding()) {
        space = this.props.spaces.find((s) => s.id === id)
      } else {
        space = this.state.spacesWithGeometries.find((g) => g.space_id === id)
      }

      const spaceData = this.bulkAdding()
        ? this.defaultSpaceGeometryData()
        : space.data
      const calculateSeparation =
        (separation + parseInt(spaceData.height)) * index
      const newSpaceData = {
        x: parseInt(spaceData.x),
        y: parseInt(spaceData.y + calculateSeparation),
        height: parseInt(spaceData.height),
        width: parseInt(spaceData.width),
        rotation: parseInt(spaceData.rotation),
      }
      if (!this.bulkAdding()) {
        spacesWithGeometries = spacesWithGeometries.filter(
          (space) => space.space_id !== id
        )
      }
      return {
        ...space,
        space_id: id,
        data: this.bulkAdding() ? newSpaceData : spaceData,
        bulkSpace: true,
      }
    })

    const formData = {
      x: spaceGeometries[0].data.x,
      y: spaceGeometries[0].data.y,
      width: spaceGeometries[0].data.width,
      height: spaceGeometries[0].data.height,
      rotation: spaceGeometries[0].data.rotation,
      separation,
      bulkActionOrientation: 0,
    }

    this.setState({
      spacesWithGeometries: [...spacesWithGeometries, ...spaceGeometries],
      editing: true,
      formData,
      boundingRectData: this.calculateBoundingRectData(spaceGeometries),
    })
  }

  handleSelectAllSpaces() {
    let spaceIds = this.bulkAdding()
      ? this.spaces().unannotatedSpaces.map((space) => space.id)
      : this.spaces().annotatedSpaces.map((space) => space.id)
    if (this.state.bulkAddSpaces.length === spaceIds.length) {
      spaceIds = []
    }
    this.setState({ bulkAddSpaces: spaceIds })
  }

  isDiagonal(degrees) {
    return (
      (degrees >= 45 && degrees <= 135) || (degrees >= 225 && degrees <= 315)
    )
  }

  isHorizontalOrVertical(degrees) {
    return (
      (degrees >= 0 && degrees <= 45) ||
      (degrees >= 135 && degrees <= 225) ||
      (degrees >= 315 && degrees <= 360)
    )
  }

  /**
   *
   * @param {*} index
   * @param {*} deltaValue
   * @param {*} orientation
   * @returns dynamicXSeparation, dynamicYSeparation
   * takes in an index, a delta value, and an orientation and calculates the separation between space geometries based on the orientation of the space geometries.
   */
  calculateBulkSeparation(index, deltaValue, orientation) {
    let dynamicXSeparation = 0
    let dynamicYSeparation = 0
    const orientationDegrees = parseInt(orientation || 0)

    if (this.isDiagonal(orientationDegrees)) {
      dynamicXSeparation = deltaValue
    } else if (this.isHorizontalOrVertical(orientationDegrees)) {
      dynamicYSeparation = deltaValue
    }

    return {
      x: parseInt(dynamicXSeparation * index),
      y: parseInt(dynamicYSeparation * index),
    }
  }

  /**
   *
   * @param {string} x
   * @param {string} y
   * @param {number} deltaOrientation
   * @param {array} spaces
   * @returns rotatedX, rotatedY
   * takes in an x and y coordinate, a delta orientation, and an array of space geometries and calculates the new x and y coordinates based on the delta orientation.
   */
  calculateBulkOrientation(x, y, deltaOrientation, spaces) {
    const centerSpace = spaces[Math.floor(spaces.length / 2)]
    const boundingBoxCenterX = parseInt(centerSpace.data.x)
    const boundingBoxCenterY = parseInt(centerSpace.data.y)

    const relativeX = parseInt(x) - boundingBoxCenterX
    const relativeY = parseInt(y) - boundingBoxCenterY

    const radians = (deltaOrientation * Math.PI) / 180
    const rotatedX =
      relativeX * Math.cos(radians) - relativeY * Math.sin(radians)
    const rotatedY =
      relativeX * Math.sin(radians) + relativeY * Math.cos(radians)

    return {
      x: Math.round(rotatedX + boundingBoxCenterX),
      y: Math.round(rotatedY + boundingBoxCenterY),
    }
  }

  handleInputChange(event) {
    const target = event.target
    const value = target.value
    const name = target.name
    const computedValue = value === "" ? 0 : parseInt(value)
    const deltaValue = computedValue - this.state.formData[name]

    if (this.state.performingBulkAction) {
      const bulkAddSpaces = this.state.spacesWithGeometries.filter(
        (space) => space.bulkSpace
      )
      const spacesWithGeometries = bulkAddSpaces.map((space, index) => {
        if (name === "x") {
          return {
            ...space,
            data: {
              ...space.data,
              [name]: space.data[name] + deltaValue,
            },
          }
        } else if (name === "separation") {
          const deltaValue = computedValue - this.state.formData[name]
          const rotationDegrees = this.state.formData.bulkActionOrientation
          const newSeparation = this.calculateBulkSeparation(
            index,
            deltaValue,
            rotationDegrees
          )
          return {
            ...space,
            data: {
              ...space.data,
              x: space.data.x + newSeparation.x,
              y: space.data.y + newSeparation.y,
            },
          }
        } else if (name === "bulkActionOrientation") {
          const deltaOrientation =
            computedValue - parseInt(this.state.formData[name] || 0)
          const newOrientation = this.calculateBulkOrientation(
            space.data.x,
            space.data.y,
            deltaOrientation,
            bulkAddSpaces
          )
          return {
            ...space,
            data: {
              ...space.data,
              x: newOrientation.x,
              y: newOrientation.y,
            },
          }
        } else if (name === "y") {
          if (
            this.isHorizontalOrVertical(
              this.state.formData.bulkActionOrientation
            )
          ) {
            return {
              ...space,
              data: {
                ...space.data,
                [name]: space.data[name] - deltaValue,
              },
            }
          } else {
            return {
              ...space,
              data: {
                ...space.data,
                [name]: computedValue,
              },
            }
          }
        } else {
          return {
            ...space,
            data: {
              ...space.data,
              [name]: computedValue,
            },
          }
        }
      })
      this.setState({
        spacesWithGeometries: [
          ...this.state.spacesWithGeometries.filter(
            (space) => !space.bulkSpace
          ),
          ...spacesWithGeometries,
        ],
        boundingRectData: this.calculateBoundingRectData(spacesWithGeometries),
      })
    }
    this.setState({
      formData: {
        ...this.state.formData,
        [name]: value,
      },
    })
  }

  renderBulkActions() {
    if (this.bulkAdding()) {
      return (
        <div className="flex w-full flex-col">
          <div className="flex w-full justify-between pb-2">
            <Button type="tertiary" onClick={this.cancelBulkAction}>
              Back
            </Button>
            <FormGroup
              inputType="checkbox"
              name="selectAllSpaces"
              label="Select All"
              onChange={this.handleSelectAllSpaces}
              value={
                this.state.bulkAddSpaces.length ===
                this.spaces().unannotatedSpaces.length
              }
            />
            <Button
              type="primary"
              disabled={this.state.bulkAddSpaces.length === 0}
              onClick={this.handleBulkAction}
            >
              Next
            </Button>
          </div>
          {this.spaces().unannotatedSpaces.map((space) => {
            return (
              <FormGroup
                key={space.id}
                inputType="checkbox"
                name={`bulkAction-${space.id}`}
                label={space.name}
                value={this.state.bulkAddSpaces.includes(space.id)}
                onChange={() => this.toggleBulkAddSpace(space.id)}
              />
            )
          })}
        </div>
      )
    } else {
      return (
        <div className="flex w-full flex-col">
          <div className="flex w-full justify-between pb-2">
            <Button
              type="tertiary"
              onClick={() => this.setState(this.cancelBulkAction)}
            >
              Back
            </Button>
            <FormGroup
              inputType="checkbox"
              name="selectAllSpaces"
              label="Select All"
              onChange={this.handleSelectAllSpaces}
              value={
                this.state.bulkAddSpaces.length ===
                this.spaces().unannotatedSpaces.length
              }
            />
            <Button
              type="primary"
              disabled={this.state.bulkAddSpaces.length === 0}
              onClick={this.handleBulkAction}
            >
              Next
            </Button>
          </div>
          {this.spaces().annotatedSpaces.map((space) => {
            return (
              <FormGroup
                key={space.id}
                inputType="checkbox"
                name={`bulkAction-${space.id}`}
                label={space.name}
                value={this.state.bulkAddSpaces.includes(space.id)}
                onChange={() => this.toggleBulkAddSpace(space.id)}
              />
            )
          })}
        </div>
      )
    }
  }

  renderSidebarContent() {
    if (this.state.editing) {
      return this.renderGeometryForm()
    } else if (this.state.performingBulkAction) {
      return this.renderBulkActions()
    } else {
      return this.renderSpaces()
    }
  }

  renderSidebar() {
    return (
      <div
        className="flex flex-shrink-0 flex-grow-0 overflow-y-auto"
        style={{ flexBasis: 300, height: "calc(100vh - 80px)" }}
      >
        <div
          className="card flex overflow-y-auto overflow-x-hidden lg:w-full"
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: this.state.canvasHeight }}
        >
          {this.renderSidebarContent()}
        </div>
      </div>
    )
  }

  renderHeader() {
    let cta
    if (isPresent(this.props.map.published_at)) {
      cta = (
        <a
          href={this.props.settingsEndPoint}
          className="btn btn-secondary w-full"
        >
          Done
        </a>
      )
    } else {
      cta = (
        <Button
          type="secondary"
          className="w-full"
          processing={this.state.publishingMap}
          onClick={this.publishMap}
        >
          Publish
        </Button>
      )
    }

    return (
      <div className="navbar-default flex h-16 w-full flex-row items-center justify-between">
        <div>
          <a href={this.props.settingsEndPoint} className="btn btn-secondary">
            <i className="icon icon-chevron-left mr-1 text-xs" />
            Back to Settings
          </a>
        </div>
        <div className="text-gray-200">
          <div className="text-xl">{this.props.map.name}</div>
        </div>
        <div className="mr-4">{cta}</div>
      </div>
    )
  }

  // separate the spaces into two lists, annotated and unannotated
  spaces() {
    const annotatedSpaceIds = this.state.spacesWithGeometries.map(
      (item) => item.space_id
    )
    const [annotatedSpaces, unannotatedSpaces] = partition(
      this.props.spaces,
      (space) => annotatedSpaceIds.includes(space.id)
    )
    return { annotatedSpaces, unannotatedSpaces }
  }

  renderUnannotatedSpaces(spaces) {
    if (spaces.length === 0) return null

    return (
      <div className="pt-4">
        <p className="text-lg font-bold">Un-Annotated Spaces</p>
        <Button
          type="tertiary"
          className="mb-2 w-full"
          onClick={() => {
            this.setState({ performingBulkAction: true, bulkAction: "add" })
          }}
        >
          Bulk Add
        </Button>
        {spaces.map((space) => {
          return (
            <div
              key={space.id}
              className="flex flex-row justify-between lg:h-7 lg:w-64"
            >
              <p className="lg:w-3/4 lg:truncate">{space.name}</p>
              <Button
                type="ghost"
                className="btn-sm font-bold"
                onClick={() => this.editingNewSpace(space)}
              >
                Add
              </Button>
            </div>
          )
        })}
      </div>
    )
  }

  renderAnnotatedSpaces(spaces) {
    if (spaces.length === 0) return null

    return (
      <div className="pt-4">
        <p className="text-lg font-bold">Annotated Spaces</p>
        <Button
          type="tertiary"
          className="mb-2 w-full"
          onClick={() => {
            this.setState({ performingBulkAction: true, bulkAction: "edit" })
          }}
        >
          Bulk Edit
        </Button>
        {spaces.map((space) => {
          const savedSpace = this.state.spacesWithGeometries.find(
            (g) => g.space_id === space.id
          )
          return (
            <div
              key={space.id}
              className="flex flex-row justify-between lg:h-7 lg:w-64"
            >
              <p className="lg:w-3/4 lg:truncate">{space.name}</p>
              <div>
                <Button
                  type="ghost"
                  className="btn-sm font-bold"
                  onClick={() => this.editingExistingSpace(savedSpace)}
                >
                  Edit
                </Button>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  renderSpaces() {
    const spaces = this.spaces()
    return (
      <div className="space-y-4 divide-y px-2 pt-1">
        <p className="text-muted">
          Complete the map by adding spaces to their correct locations.
        </p>
        {this.renderUnannotatedSpaces(spaces.unannotatedSpaces)}
        {this.renderAnnotatedSpaces(spaces.annotatedSpaces)}
      </div>
    )
  }

  renderGeometryForm() {
    const spaceGroupHeader =
      this.state.spaceEditing && this.state.spaceEditing.spaceGroup
        ? `This space belongs to the ${this.state.spaceEditing.spaceGroup.name} space group.`
        : "This space does not belong to a space group."
    const viewBoxHeight = MarinaMapView.viewBoxDimensions.height
    const viewBoxWidth = MarinaMapView.viewBoxDimensions.width
    return (
      <div className="w-screen">
        <div>
          {this.state.spaceEditing && (
            <p className="text-lg font-bold">{this.state.spaceEditing.name}</p>
          )}
          <p className="text-muted">
            {this.state.performingBulkAction
              ? "Performing bulk action"
              : spaceGroupHeader}
          </p>
        </div>

        <div>
          <div className="flex h-6 w-full items-center rounded-sm bg-gray-200 py-3 pl-1 font-bold">
            <p className="my-2">Location</p>
          </div>
          <div className="row form-row pt-2">
            <div className="col-md-6">
              <FormGroup
                inputType="num-with-icon"
                name="x"
                label="X"
                value={this.state.formData.x}
                onChange={this.handleInputChange}
                iconText="px"
                min={0}
                max={this.state.canvasWidth}
                validation={this.validator.message(
                  "x",
                  this.state.formData.x,
                  `required|numeric|between:0,${viewBoxWidth},num`
                )}
              />
            </div>
            <div className="col-md-6">
              <FormGroup
                inputType="num-with-icon"
                name="y"
                label="Y"
                value={this.state.formData.y}
                onChange={this.handleInputChange}
                iconText="px"
                min={0}
                max={this.state.canvasHeight}
                validation={this.validator.message(
                  "y",
                  this.state.formData.y,
                  `required|numeric|between:0,${viewBoxHeight},num`
                )}
              />
            </div>
          </div>
          <div>
            <div className="flex h-6 w-full items-center rounded-sm bg-gray-200 py-3 pl-1 font-bold">
              <p className="my-2">Size</p>
            </div>
            <div className="row form-row pt-2">
              <div className="col-md-6">
                <FormGroup
                  inputType="num-with-icon"
                  name="height"
                  label="Height"
                  value={this.state.formData.height}
                  onChange={this.handleInputChange}
                  iconText="px"
                  min={0}
                  max={this.state.canvasHeight}
                  validation={this.validator.message(
                    "height",
                    this.state.formData.height,
                    "required|numeric|min:0,num"
                  )}
                />
              </div>
              <div className="col-md-6">
                <FormGroup
                  inputType="num-with-icon"
                  name="width"
                  label="Width"
                  value={this.state.formData.width}
                  iconText="px"
                  onChange={this.handleInputChange}
                  min={0}
                  max={this.state.canvasWidth}
                  validation={this.validator.message(
                    "width",
                    this.state.formData.width,
                    "required|numeric|min:0,num"
                  )}
                />
              </div>
            </div>
          </div>
          <div>
            <div className="flex h-6 w-full items-center rounded-sm bg-gray-200 py-3 pl-1 font-bold">
              <p className="my-2" id="rotation">
                Rotation
              </p>
            </div>
            <div className="row form-row pt-2">
              <div className="col-md-6">
                <FormGroup
                  inputType="num-with-icon"
                  name="rotation"
                  ariaLabelledBy="rotation"
                  value={this.state.formData.rotation}
                  min={0}
                  max={360}
                  iconText="degrees"
                  onChange={this.handleInputChange}
                  validation={this.validator.message(
                    "rotation",
                    this.state.formData.rotation,
                    "required|numeric|between:0,360,num"
                  )}
                />
              </div>
              <div className="col-md-6">
                <Button
                  className="w-full"
                  type="primary"
                  onClick={this.flipBoatOrientation}
                >
                  Flip Space
                </Button>
              </div>
            </div>
          </div>
          {this.state.performingBulkAction && (
            <div>
              <div className="flex h-6 w-full items-center rounded-sm bg-gray-200 py-3 pl-1 font-bold">
                <p className="my-2" id="bulk-actions">
                  Bulk Actions
                </p>
              </div>
              <div className="row form-row pt-2">
                <div className="col-md-6">
                  <FormGroup
                    inputType="num-with-icon"
                    name="separation"
                    label="Separation"
                    value={this.state.formData.separation}
                    min={0}
                    max={this.state.canvasHeight}
                    iconText="px"
                    onChange={this.handleInputChange}
                    validation={this.validator.message(
                      "separation",
                      this.state.formData.separation,
                      "required|numeric|min:0,num"
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <FormGroup
                    inputType="num-with-icon"
                    name="bulkActionOrientation"
                    label="Orientation"
                    value={this.state.formData.bulkActionOrientation}
                    onChange={this.handleInputChange}
                    iconText="degrees"
                    min={0}
                    max={360}
                    validation={this.validator.message(
                      "orientation",
                      this.state.formData.bulkActionOrientation,
                      "required|numeric|between:0,360,num"
                    )}
                  />
                </div>
              </div>
            </div>
          )}
          {this.state.spaceEditing && this.state.spaceEditing.spaceGroup
            ? this.renderSpaceGroupData(this.state.spaceEditing.spaceGroup)
            : null}
          {this.state.spaceEditing && !this.state.performingBulkAction && (
            <div>
              <div className="mt-6 h-0.5 w-full bg-gray-200 pl-1" />
              <div className="mt-2 flex flex-row justify-evenly pb-1">
                <Button
                  type="tertiary"
                  className="mr-2 w-full"
                  disabled={
                    this.state.savingSpace || this.state.destroyingSpaceGeometry
                  }
                  onClick={this.cancelEdit}
                >
                  Back
                </Button>
                {this.state.savedSpace ? (
                  <Button
                    type="danger"
                    className="w-full"
                    disabled={this.state.savingSpace}
                    processing={this.state.destroyingSpaceGeometry}
                    processingText={"Deleting"}
                    onClick={() =>
                      this.destroySpaceGeometry(this.state.spaceEditing)
                    }
                  >
                    Delete
                  </Button>
                ) : null}
              </div>
              <div>
                <Button
                  type="primary"
                  className="w-full"
                  disabled={this.state.destroyingSpaceGeometry}
                  processing={this.state.savingSpace}
                  processingText={
                    this.state.spaceEditing.newSpace ? "Saving" : "Updating"
                  }
                  onClick={this.saveSpace}
                >
                  {this.state.spaceEditing.newSpace
                    ? "Save (enter)"
                    : "Update  (enter)"}
                </Button>
              </div>
            </div>
          )}
          {this.state.performingBulkAction && (
            <div>
              <div className="flex w-full flex-col space-y-2">
                <div className="mt-2 flex flex-row justify-evenly pb-1">
                  <Button
                    type="tertiary"
                    className="mr-2 w-full"
                    disabled={
                      this.state.bulkAddSpaces.length === 0 ||
                      this.state.savingSpace
                    }
                    onClick={this.cancelBulkAction}
                  >
                    Cancel
                  </Button>
                  {!this.bulkAdding() && (
                    <Button
                      type="danger"
                      className="w-full"
                      disabled={this.state.savingSpace}
                      processing={this.state.destroyingSpaceGeometry}
                      processingText={"Deleting"}
                      onClick={this.handleBulkDestroy}
                    >
                      Delete
                    </Button>
                  )}
                </div>
                <Button
                  type="primary"
                  className="w-full"
                  disabled={this.state.bulkAddSpaces.length === 0}
                  processing={this.state.savingSpace}
                  processingText={this.bulkAdding() ? "Saving" : "Updating"}
                  onClick={this.handleBulkSave}
                >
                  {this.bulkAdding() ? "Save" : "Update"} (enter)
                </Button>
              </div>
              <div className="flex flex-col space-y-2">
                {this.state.spacesWithGeometries.map((space) => {
                  if (space.bulkSpace) {
                    return (
                      <div key={space.space_id} className="flex flex-col">
                        <p className="my-2 font-semibold">{space.name}</p>
                        <div className="flex space-x-2">
                          <span>
                            <strong>X:</strong> {space.data.x}
                          </span>
                          <span>
                            <strong>Y:</strong> {space.data.y}
                          </span>
                          <span>
                            <strong>Height:</strong> {space.data.height}
                          </span>
                          <span>
                            <strong>Width:</strong> {space.data.width}
                          </span>
                        </div>
                      </div>
                    )
                  } else {
                    return null
                  }
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  renderSpaceGroupData(spaceGroup) {
    return (
      <div>
        <div className="mt-6 flex h-6 w-full items-center rounded-sm bg-gray-200 py-3 pl-1 font-bold">
          <p className="my-2" id="rotation">
            Space Group Details
          </p>
        </div>
        <div className="row form-row p-2">
          <div className="col-md-9">
            <p className="font-bold">Space Group</p>
          </div>
          <div className="col-md-3 text-end">
            <p>{spaceGroup.name}</p>
          </div>
        </div>
        <div className="row form-row p-2">
          <div className="col-md-9">
            <p className="font-bold">LOA</p>
          </div>
          <div className="col-md-3 text-end">
            <p>{`${inchesToFeet(spaceGroup.minLength)}' - ${inchesToFeet(
              spaceGroup.maxLength
            )}'`}</p>
          </div>
        </div>
        <div className="row form-row p-2">
          <div className="col-md-9">
            <p className="font-bold">Beam</p>
          </div>
          <div className="col-md-3 text-end">
            <p>{`${inchesToFeet(spaceGroup.minBeam)}' - ${inchesToFeet(
              spaceGroup.maxBeam
            )}'`}</p>
          </div>
        </div>
        <div className="row form-row p-2">
          <div className="col-md-9">
            <p className="font-bold">Draft</p>
          </div>
          <div className="col-md-3 text-end">
            <p>{`${inchesToFeet(spaceGroup.minDraft)}' - ${inchesToFeet(
              spaceGroup.maxDraft
            )}'`}</p>
          </div>
        </div>
      </div>
    )
  }

  render() {
    return (
      <div className="lg:absolute lg:left-0" style={{ width: "99.2vw" }}>
        {this.renderHeader()}
        <div className="mt-2 flex flex-col sm:flex-row sm:items-start sm:justify-between">
          <MarinaMapView
            mapId={this.props.map.id}
            mapImage={this.props.mapImage}
            spaceEditing={this.state.spaceEditing}
            editing={this.state.editing}
            performingBulkAction={this.state.performingBulkAction}
            boatImage={this.props.boatImage}
            linearDockImage={this.props.linearDockImage}
            spaceGeometry={
              this.state.performingBulkAction
                ? this.state.boundingRectData
                : this.state.formData
            }
            spacesWithGeometries={this.state.spacesWithGeometries}
            setFormState={this.setFormState}
            editingExistingSpace={this.editingExistingSpace}
          />
          {this.renderSidebar()}
        </div>
      </div>
    )
  }
}

MarinaMapAnnotationController.propTypes = {
  map: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    published_at: PropTypes.string,
  }).isRequired,
  savedSpaceGeometries: PropTypes.array.isRequired,
  spaces: PropTypes.array.isRequired,
  mapImage: PropTypes.string.isRequired,
  settingsEndPoint: PropTypes.string.isRequired,
  geometryEndpoint: PropTypes.string.isRequired,
  boatImage: PropTypes.string.isRequired,
  linearDockImage: PropTypes.string.isRequired,
  publishMapEndpoint: PropTypes.string.isRequired,
  bulkCreateGeometryEndpoint: PropTypes.string,
  bulkUpdateGeometryEndpoint: PropTypes.string,
  bulkDestroyGeometryEndpoint: PropTypes.string,
}

export default MarinaMapAnnotationController
